<template>
    <div class="page-content-wrapper">
        <h3 class="title">{{ $t("tables.tables") }}</h3>

        <div class="table-wrapper">
            <div class="toolbar">
                <div class="left">
                    <a-input-search
                        style="width: 300px"
                        v-model="searchText"
                        :placeholder="$t('message.search')"
                        class="row search-input"
                        @input="onSearch"
                    />
                </div>
                <div class="right">
                    <SupsisButton primary @click="$feature.beforeCreateTable(createTable)">
                        <i class="material-icons">add</i>
                        {{ $t("message.newTable") }}
                    </SupsisButton>
                    <SupsisButton class="ai-suggest-button" @click="showWizard">
                        <img src="/static/img/copilot.png" class="copilot-icon" />
                        Supsis Asistan ile Uygulama Oluştur
                    </SupsisButton>
                </div>
            </div>

            <SupsisTable
                :columns="getCols()"
                :rows="paginatedTables"
                :top-pagination="true"
                empty-data-bordered
                :bottom-pagination="false"
                :pagination-cursor="pagination"
                :options="{
                    rowStyleOption,
                    clipboardOption,
                    fixedHeader: true,
                    id: 'tables-table',
                    borderX: true,
                    borderY: true,
                }"
                @cursor-changed="(cursor) => (pagination = cursor)"
            />
        </div>

        <Modal2
            v-if="showCreateModal"
            @cancel="showCreateModal = false"
            :showFooter="false"
            :title="$t('tables.createTable')"
        >
            <CreateTable @create="handleCreateTable" />
        </Modal2>

        <Modal2
            v-if="showEditModal"
            @cancel="showEditModal = false"
            :showFooter="false"
            :title="$t('tables.editTable')"
        >
            <EditTable :table="editingTable" @update="handleUpdateTable" />
        </Modal2>
        <TableAiWizard v-if="showTableAiWizard" @table-added="tableAdded" @close="showTableAiWizard = false" />
    </div>
</template>

<script>
import { TABLE_API } from "../../../api/table";
import SupsisTable from "../../../components/SupsisTable.vue";
import SupsisButton from "../Settings/components/SupsisButton.vue";
import Modal2 from "../../../components/common/Modal2.vue";
import CreateTable from "./CreateTable.vue";
import EditTable from "./EditTable.vue";
import { mapActions } from "vuex";
import TableAiWizard from "./TableAiWizard.vue";

export default {
    name: "TableList",
    components: {
        TableAiWizard,
        SupsisTable,
        SupsisButton,
        Modal2,
        CreateTable,
        EditTable,
    },
    data() {
        return {
            searchText: "",
            tables: [],
            pagination: {
                page: 1,
                perPage: 20,
                total: 0,
            },
            loading: true,
            rowStyleOption: {
                stripe: true,
            },
            clipboardOption: {
                copy: true,
                paste: false,
                cut: false,
                delete: false,
            },
            showCreateModal: false,
            newTable: {
                name: "",
                description: "",
            },
            showEditModal: false,
            editingTable: null,
            showTableAiWizard: false,
        };
    },
    computed: {
        filteredTables() {
            if (!this.searchText) {
                return this.tables;
            }
            const searchLower = this.searchText.toLowerCase();
            return this.tables.filter(
                (table) =>
                    table.name.toLowerCase().includes(searchLower) ||
                    table.description.toLowerCase().includes(searchLower)
            );
        },
        paginatedTables() {
            const start = (this.pagination.page - 1) * this.pagination.perPage;
            const end = start + this.pagination.perPage;
            return this.filteredTables.slice(start, end);
        },
        totalPages() {
            return Math.ceil(this.filteredTables.length / this.pagination.perPage);
        },
    },
    methods: {
        ...mapActions({
            fetchCustomerTables: "table/fetchCustomerTables",
            deleteCustomerTable: "table/deleteCustomerTable",
        }),
        tableAdded(table) {
            this.tables = this.tables.filter((t) => t._id !== table._id);
            this.tables = [...this.tables, table];
            this.pagination.total = this.tables.length;
        },
        showWizard() {
            this.showTableAiWizard = true;
        },

        async fetchTables() {
            try {
                this.loading = true;
                const { total, tables } = await this.fetchCustomerTables();
                this.tables = tables;
                this.pagination.total = total;
                this.loading = false;
            } catch (error) {
                console.error("Error fetching tables:", error);
                this.loading = false;
            }
        },
        onSearch() {
            this.pagination.page = 1;
        },
        getCols() {
            return [
                {
                    key: "name",
                    field: "name",
                    title: this.$t("message.name"),
                },
                {
                    key: "description",
                    field: "description",
                    title: this.$t("message.description"),
                },
                {
                    key: "createdAt",
                    field: "createdAt",
                    title: this.$t("tables.creationDate"),
                    renderBodyCell: ({ row }) => {
                        return this.$moment(row.createdAt).format("DD.MM.YYYY");
                    },
                },
                {
                    key: "actions",
                    field: "actions",
                    title: this.$t("message.actions"),
                    renderBodyCell: ({ row }) => {
                        return (
                            <div class="actions">
                                <SupsisButton
                                    class="edit-btn"
                                    onClick={() => this.editTable(row._id)}
                                    ghost
                                    icon="edit"
                                />
                                <SupsisButton
                                    class="view-btn"
                                    onClick={() => this.viewTable(row._id)}
                                    ghost
                                    icon="visibility"
                                />
                                <SupsisButton
                                    class="delete-btn"
                                    style={{ padding: "16.5px" }}
                                    onClick={() => this.deleteTable(row._id)}
                                    danger
                                    ghost
                                    icon="delete"
                                />
                            </div>
                        );
                    },
                },
            ];
        },

        createTable() {
            this.showCreateModal = true;
        },
        async handleCreateTable(data) {
            try {
                const response = await TABLE_API.createTable(data.name, data.description, data.template);
                this.showCreateModal = false;
                this.fetchTables();
                this.viewTable(response._id);
            } catch (error) {
                console.error("Error creating table:", error);
            }
        },
        viewTable(tableId) {
            this.$router.push(`/tables/t/${tableId}`);
        },
        async deleteTable(tableId) {
            try {
                await this.$confirm({
                    title: this.$t("tables.deleteTable"),
                    content: this.$t("tables.sureDeleteTable"),
                    okText: this.$t("message.yes"),
                    okType: "danger",
                    cancelText: this.$t("message.no"),
                    onOk: async () => {
                        await this.deleteCustomerTable(tableId);
                        this.tables = this.tables.filter((table) => table._id !== tableId);
                        this.$message.success(this.$t("tables.deleteTableSucs"));
                    },
                });
            } catch (error) {
                console.error(this.$t("tables.deleteTableError"), error);
            }
        },
        editTable(tableId) {
            const table = this.tables.find((table) => table._id === tableId);
            if (table) {
                this.editingTable = { ...table };
                this.showEditModal = true;
            }
        },
        async handleUpdateTable(data) {
            try {
                await TABLE_API.updateTable(this.editingTable._id, data);
                this.showEditModal = false;
                this.fetchTables();
            } catch (error) {
                console.error("Error updating table:", error);
            }
        },
        prevPage() {
            if (this.pagination.page > 1) {
                this.pagination.page--;
            }
        },
        nextPage() {
            if (this.pagination.page < this.totalPages) {
                this.pagination.page++;
            }
        },
    },
    created() {
        this.fetchTables();
    },
    watch: {
        pagination: {
            handler() {
                this.fetchTables();
            },
            deep: true,
        },
    },
};
</script>

<style scoped lang="scss">
.page-content-wrapper {
    display: flex;
    flex-direction: column;
    gap: 5px;
    padding: 10px;

    .table-wrapper {
        display: flex;
        flex-direction: column;
        gap: 5px;

        .toolbar {
            width: 100%;
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 10px 0;
            gap: 5px;

            .left {
                display: flex;
                gap: 5px;
                align-items: center;
            }

            .right {
                display: flex;
                gap: 5px;

                .copilot-icon {
                    vertical-align: middle;
                    margin-right: 0px;
                    width: 15px;
                    height: 15px;
                }
            }
        }
    }

    #tables-table {
        .actions {
            display: flex;
            gap: 5px;
        }
    }

}
</style>
